/**
 * Centralized Brand Icon Utility
 * 
 * RULE: Brand logos display in their official brand colors.
 *       Generic icons use the app's theme color.
 * 
 * This ensures consistency across all pages of the application.
 */

import {
  Smartphone,
  Tablet,
  Laptop,
  HardDrive,
  Wifi,
  Tv,
  Mail,
  Globe,
  Building2,
  CreditCard,
  Banknote,
  PiggyBank,
  TrendingUp,
  Bitcoin,
  Shield,
  Home,
  Car,
  Heart,
  Zap,
  Droplets,
  Phone,
  Briefcase,
  ShoppingCart,
  Megaphone,
  Award,
  Hospital,
  Pill,
  Users,
  MessageCircle,
  FileCheck,
  Receipt,
  Scale,
  Key,
  FolderPlus,
  Folder,
  Share2,
  Server,
  Cloud,
  Newspaper,
  Music,
  Gamepad2,
  Plane,
  DollarSign,
  KeyRound,
  MessageSquare,
  Lock,
} from "lucide-react";

import {
  SiGoogle,
  SiApple,
  SiFacebook,
  SiInstagram,
  SiX,
  SiTiktok,
  SiSnapchat,
  SiReddit,
  SiDiscord,
  SiWhatsapp,
  SiZoom,
  SiPaypal,
  SiVenmo,
  SiCashapp,
  SiStripe,
  SiShopify,
  SiEtsy,
  SiEbay,
  SiCoinbase,
  SiBinance,
  SiQuickbooks,
  SiGithub,
  SiNetflix,
  SiSpotify,
  SiYoutubemusic,
} from "react-icons/si";
// SiLinkedin, SiAmazon, SiPrimevideo removed in react-icons v5 — use Globe fallback
const SiLinkedin = Globe;
const SiAmazon = Globe;
const SiPrimevideo = Globe;
// SiSlack removed in react-icons 5.7.0 (Simple Icons dropped Slack, keeping only Slackware) — messaging fallback
const SiSlack = MessageSquare;
import { FaMicrosoft } from "react-icons/fa";

/**
 * Brand icons with their official brand colors
 * These icons will NOT use the app's theme color
 */
const BRAND_ICON_CONFIG: Record<string, { component: React.ComponentType<any>; color: string }> = {
  SiGoogle: { component: SiGoogle, color: "#4285F4" },
  SiApple: { component: SiApple, color: "#000000" },
  SiFacebook: { component: SiFacebook, color: "#1877F2" },
  SiInstagram: { component: SiInstagram, color: "#E4405F" },
  SiX: { component: SiX, color: "#000000" },
  SiTiktok: { component: SiTiktok, color: "#000000" },
  SiSnapchat: { component: SiSnapchat, color: "#FFFC00" },
  SiLinkedin: { component: SiLinkedin, color: "#0A66C2" },
  SiReddit: { component: SiReddit, color: "#FF4500" },
  SiDiscord: { component: SiDiscord, color: "#5865F2" },
  SiWhatsapp: { component: SiWhatsapp, color: "#25D366" },
  SiSlack: { component: SiSlack, color: "#4A154B" },
  SiZoom: { component: SiZoom, color: "#2D8CFF" },
  SiPaypal: { component: SiPaypal, color: "#00457C" },
  SiVenmo: { component: SiVenmo, color: "#3D95CE" },
  SiCashapp: { component: SiCashapp, color: "#00D632" },
  SiStripe: { component: SiStripe, color: "#635BFF" },
  SiShopify: { component: SiShopify, color: "#7AB55C" },
  SiEtsy: { component: SiEtsy, color: "#F16521" },
  SiAmazon: { component: SiAmazon, color: "#FF9900" },
  SiEbay: { component: SiEbay, color: "#E53238" },
  SiCoinbase: { component: SiCoinbase, color: "#0052FF" },
  SiBinance: { component: SiBinance, color: "#F0B90B" },
  SiQuickbooks: { component: SiQuickbooks, color: "#2CA01C" },
  SiGithub: { component: SiGithub, color: "#181717" },
  SiNetflix: { component: SiNetflix, color: "#E50914" },
  SiPrimevideo: { component: SiPrimevideo, color: "#00A8E1" },
  SiSpotify: { component: SiSpotify, color: "#1DB954" },
  SiYoutubemusic: { component: SiYoutubemusic, color: "#FF0000" },
  FaMicrosoft: { component: FaMicrosoft, color: "#00A4EF" },
};

/**
 * Generic icons that use the app's theme color
 */
const GENERIC_ICON_MAP: Record<string, React.ComponentType<any>> = {
  Smartphone,
  Tablet,
  Laptop,
  HardDrive,
  Wifi,
  Tv,
  Mail,
  Globe,
  Building2,
  CreditCard,
  Banknote,
  PiggyBank,
  TrendingUp,
  Bitcoin,
  Shield,
  Home,
  Car,
  Heart,
  Zap,
  Droplets,
  Phone,
  Briefcase,
  ShoppingCart,
  Megaphone,
  Award,
  Hospital,
  Pill,
  Users,
  MessageCircle,
  FileCheck,
  Receipt,
  Scale,
  Key,
  FolderPlus,
  Folder,
  Share2,
  Server,
  Cloud,
  Newspaper,
  Music,
  Gamepad2,
  Plane,
  DollarSign,
  KeyRound,
  MessageSquare,
  Lock,
};

/**
 * Check if an icon name corresponds to a brand icon
 */
export function isBrandIcon(iconName: string | null | undefined): boolean {
  if (!iconName) return false;
  return iconName in BRAND_ICON_CONFIG;
}

/**
 * Get an icon by name with proper color handling:
 * - Brand icons: Display in their official brand colors
 * - Generic icons: Display in the app's theme color (or custom color class)
 * 
 * @param iconName - The name of the icon (e.g., "SiGoogle", "Smartphone", "Key")
 * @param options - Optional configuration
 * @param options.size - Size class for the icon (default: "h-4 w-4")
 * @param options.themeColorClass - Color class for generic icons (default: "text-primary")
 * @returns JSX.Element - The rendered icon
 */
export function getIcon(
  iconName: string | null | undefined,
  options?: {
    size?: string;
    themeColorClass?: string;
  }
): JSX.Element {
  const { size = "h-4 w-4", themeColorClass = "text-primary" } = options || {};
  
  if (!iconName) {
    return <Key className={`${size} ${themeColorClass}`} />;
  }

  // Check if it's a brand icon - render with brand color
  const brandConfig = BRAND_ICON_CONFIG[iconName];
  if (brandConfig) {
    const BrandComponent = brandConfig.component;
    return (
      <BrandComponent 
        className={size}
        style={{ color: brandConfig.color }}
      />
    );
  }

  // Check if it's a generic icon - render with theme color
  const GenericComponent = GENERIC_ICON_MAP[iconName];
  if (GenericComponent) {
    return <GenericComponent className={`${size} ${themeColorClass}`} />;
  }

  // Fallback to Key icon
  return <Key className={`${size} ${themeColorClass}`} />;
}

/**
 * Get all available icon names (for dropdowns, pickers, etc.)
 */
export function getAllIconNames(): { brand: string[]; generic: string[] } {
  return {
    brand: Object.keys(BRAND_ICON_CONFIG),
    generic: Object.keys(GENERIC_ICON_MAP),
  };
}

/**
 * Legacy compatibility: Get icon by name with color class parameter
 * This matches the old getIconByName signature for easy migration
 * 
 * @deprecated Use getIcon() instead for new code
 */
export function getIconByName(
  iconName: string | null | undefined,
  colorClass: string = "text-primary"
): JSX.Element {
  return getIcon(iconName, { themeColorClass: colorClass });
}

/**
 * Smart icon matching based on account type name
 * Uses brand colors for recognized company names, theme colors for generic categories
 * 
 * @param accountType - The account type name (e.g., "Netflix Streaming", "Chase Bank")
 * @param iconMap - Optional existing icon map to check first
 * @param size - Size class for icons (default: "h-4 w-4")
 * @returns JSX.Element - The matched icon with proper colors
 */
export function getIconForAccountType(
  accountType: string,
  iconMap?: Record<string, JSX.Element>,
  size: string = "h-4 w-4"
): JSX.Element {
  // Check iconMap first if provided
  if (iconMap && iconMap[accountType]) {
    return iconMap[accountType];
  }
  
  const lower = accountType.toLowerCase();
  
  // Social Media & Messaging - brand icons with their official colors
  if (lower.includes('facebook') || lower.includes('meta')) 
    return <SiFacebook className={size} style={{ color: BRAND_ICON_CONFIG.SiFacebook.color }} />;
  if (lower.includes('instagram')) 
    return <SiInstagram className={size} style={{ color: BRAND_ICON_CONFIG.SiInstagram.color }} />;
  if (lower.includes('twitter') || lower.includes(' x ') || lower === 'x' || lower.startsWith('x ') || lower.endsWith(' x')) 
    return <SiX className={size} style={{ color: BRAND_ICON_CONFIG.SiX.color }} />;
  if (lower.includes('linkedin')) 
    return <SiLinkedin className={size} style={{ color: BRAND_ICON_CONFIG.SiLinkedin.color }} />;
  if (lower.includes('tiktok')) 
    return <SiTiktok className={size} style={{ color: BRAND_ICON_CONFIG.SiTiktok.color }} />;
  if (lower.includes('whatsapp')) 
    return <SiWhatsapp className={size} style={{ color: BRAND_ICON_CONFIG.SiWhatsapp.color }} />;
  if (lower.includes('telegram') || lower.includes('signal')) 
    return <MessageCircle className={`${size} text-blue-500`} />;
  if (lower.includes('discord')) 
    return <SiDiscord className={size} style={{ color: BRAND_ICON_CONFIG.SiDiscord.color }} />;
  if (lower.includes('slack')) 
    return <SiSlack className={size} style={{ color: BRAND_ICON_CONFIG.SiSlack.color }} />;
  if (lower.includes('snapchat')) 
    return <SiSnapchat className={size} style={{ color: BRAND_ICON_CONFIG.SiSnapchat.color }} />;
  if (lower.includes('reddit')) 
    return <SiReddit className={size} style={{ color: BRAND_ICON_CONFIG.SiReddit.color }} />;
  if (lower.includes('zoom') || lower.includes('meeting')) 
    return <SiZoom className={size} style={{ color: BRAND_ICON_CONFIG.SiZoom.color }} />;
  if (lower.includes('social') || lower.includes('messaging') || lower.includes('chat')) 
    return <MessageCircle className={`${size} text-blue-500`} />;
  
  // Shopping & Payment - brand icons
  if (lower.includes('amazon')) 
    return <SiAmazon className={size} style={{ color: BRAND_ICON_CONFIG.SiAmazon.color }} />;
  if (lower.includes('ebay') || lower.includes('auction') || lower.includes('marketplace')) 
    return <SiEbay className={size} style={{ color: BRAND_ICON_CONFIG.SiEbay.color }} />;
  if (lower.includes('paypal')) 
    return <SiPaypal className={size} style={{ color: BRAND_ICON_CONFIG.SiPaypal.color }} />;
  if (lower.includes('venmo')) 
    return <SiVenmo className={size} style={{ color: BRAND_ICON_CONFIG.SiVenmo.color }} />;
  if (lower.includes('cash app') || lower.includes('cashapp')) 
    return <SiCashapp className={size} style={{ color: BRAND_ICON_CONFIG.SiCashapp.color }} />;
  if (lower.includes('shopping') || lower.includes('retail') || lower.includes('loyalty') || lower.includes('rewards')) 
    return <ShoppingCart className={`${size} text-green-600`} />;
  
  // Crypto - brand icons
  if (lower.includes('coinbase')) 
    return <SiCoinbase className={size} style={{ color: BRAND_ICON_CONFIG.SiCoinbase.color }} />;
  if (lower.includes('binance')) 
    return <SiBinance className={size} style={{ color: BRAND_ICON_CONFIG.SiBinance.color }} />;
  if (lower.includes('crypto') || lower.includes('bitcoin') || lower.includes('wallet')) 
    return <Bitcoin className={`${size} text-orange-500`} />;
  
  // Tech & Code - brand icons
  if (lower.includes('github')) 
    return <SiGithub className={size} style={{ color: BRAND_ICON_CONFIG.SiGithub.color }} />;
  if (lower.includes('google')) 
    return <SiGoogle className={size} style={{ color: BRAND_ICON_CONFIG.SiGoogle.color }} />;
  if (lower.includes('apple') || lower.includes('icloud')) 
    return <SiApple className={size} style={{ color: BRAND_ICON_CONFIG.SiApple.color }} />;
  if (lower.includes('microsoft') || lower.includes('outlook') || lower.includes('onedrive') || lower.includes('xbox')) 
    return <FaMicrosoft className={size} style={{ color: BRAND_ICON_CONFIG.FaMicrosoft.color }} />;
  if (lower.includes('code') || lower.includes('repository') || lower.includes('gitlab') || lower.includes('bitbucket')) 
    return <Server className={`${size} text-slate-600`} />;
  if (lower.includes('domain') || lower.includes('hosting') || lower.includes('dns') || lower.includes('cloudflare')) 
    return <Globe className={`${size} text-blue-500`} />;
  if (lower.includes('quickbooks') || lower.includes('accounting')) 
    return <SiQuickbooks className={size} style={{ color: BRAND_ICON_CONFIG.SiQuickbooks.color }} />;
  
  // Streaming & Entertainment - brand icons
  if (lower.includes('netflix')) 
    return <SiNetflix className={size} style={{ color: BRAND_ICON_CONFIG.SiNetflix.color }} />;
  if (lower.includes('amazon prime') || lower.includes('prime video')) 
    return <SiPrimevideo className={size} style={{ color: BRAND_ICON_CONFIG.SiPrimevideo.color }} />;
  if (lower.includes('disney') || lower.includes('hulu') || lower.includes('streaming') || lower.includes('video')) 
    return <Tv className={`${size} text-muted-foreground`} />;
  if (lower.includes('spotify')) 
    return <SiSpotify className={size} style={{ color: BRAND_ICON_CONFIG.SiSpotify.color }} />;
  if (lower.includes('youtube music')) 
    return <SiYoutubemusic className={size} style={{ color: BRAND_ICON_CONFIG.SiYoutubemusic.color }} />;
  if (lower.includes('music') || lower.includes('apple music')) 
    return <Music className={`${size} text-green-500`} />;
  if (lower.includes('gaming') || lower.includes('xbox') || lower.includes('playstation') || lower.includes('steam')) 
    return <Gamepad2 className={`${size} text-purple-500`} />;
  if (lower.includes('news') || lower.includes('magazine') || lower.includes('subscription')) 
    return <Newspaper className={`${size} text-slate-600`} />;
  
  // Travel
  if (lower.includes('airline') || lower.includes('flight') || lower.includes('frequent flyer')) 
    return <Plane className={`${size} text-blue-500`} />;
  if (lower.includes('hotel') || lower.includes('airbnb') || lower.includes('travel')) 
    return <Building2 className={`${size} text-blue-500`} />;
  
  // Security & Password
  if (lower.includes('password manager') || lower.includes('1password') || lower.includes('lastpass') || lower.includes('bitwarden')) 
    return <KeyRound className={`${size} text-blue-600`} />;
  if (lower.includes('2fa') || lower.includes('authenticator') || lower.includes('yubikey') || lower.includes('security key')) 
    return <Lock className={`${size} text-green-600`} />;
  
  // Generic categories - theme-aware colors
  if (lower.includes('bank') || lower.includes('credit card') || lower.includes('mortgage') || lower.includes('loan')) 
    return <Building2 className={`${size} text-blue-600`} />;
  if (lower.includes('email') || lower.includes('gmail') || lower.includes('outlook') || lower.includes('yahoo') || lower.includes('hotmail')) 
    return <Mail className={`${size} text-amber-500`} />;
  if (lower.includes('phone') || lower.includes('mobile')) 
    return <Phone className={`${size} text-green-500`} />;
  if (lower.includes('insurance')) 
    return <Shield className={`${size} text-blue-600`} />;
  if (lower.includes('tax') || lower.includes('irs') || lower.includes('cra') || lower.includes('government')) 
    return <Building2 className={`${size} text-blue-700`} />;
  if (lower.includes('investment') || lower.includes('retirement') || lower.includes('401') || lower.includes('ira') || lower.includes('brokerage')) 
    return <TrendingUp className={`${size} text-green-600`} />;
  if (lower.includes('utility') || lower.includes('electric') || lower.includes('water') || lower.includes('gas')) 
    return <Zap className={`${size} text-yellow-500`} />;
  if (lower.includes('internet') || lower.includes('cable') || lower.includes('wifi') || lower.includes('provider')) 
    return <Wifi className={`${size} text-blue-500`} />;
  if (lower.includes('health') || lower.includes('medical') || lower.includes('dental') || lower.includes('vision')) 
    return <Heart className={`${size} text-primary`} />;
  if (lower.includes('work') || lower.includes('business') || lower.includes('office') || lower.includes('vpn')) 
    return <Briefcase className={`${size} text-slate-600`} />;
  
  // Default fallback
  return <Key className={`${size} text-slate-500`} />;
}
